<template>
  <div class="container py-4">
    <h1 class="mb-4">Design Studio</h1>

    <div v-if="loadingSubstrates" class="text-center">
      <b-spinner label="Loading substrates..."></b-spinner>
    </div>

    <div v-else class="mb-5">
      <div class="mb-4">
        <b-button @click="fetchTestImage" variant="outline-primary">Load Test Image</b-button>
        <div v-if="selectedImage" class="mt-3">
          <b-img :src="selectedImage.thumb" thumbnail fluid alt="Selected Image" style="max-height: 200px;"></b-img>
          <p class="small text-muted mt-1">{{ selectedImage.title }}</p>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
           <b-form-group label="Workspace/Project">
             <QuickAddWorkspace
               v-model="selectedWorkspace"
               :profile="profile"
             />
           </b-form-group>
        </div>
        <div class="col-md-6 mb-3">
          <b-form-group label="Dimensions (inches)">
            <div class="d-flex align-items-center">
              <b-form-input
                v-model="dimensions.width"
                type="number"
                placeholder="Width"
                class="mr-2"
              ></b-form-input>
              <span class="mr-2">x</span>
              <b-form-input
                v-model="dimensions.height"
                type="number"
                placeholder="Height"
              ></b-form-input>
            </div>
          </b-form-group>
        </div>
      </div>

      <b-form-group label="Select Substrate">
        <b-form-select v-model="selectedSubstrate" :options="substrateOptions">
          <template #first>
            <b-form-select-option :value="null" disabled>-- Select a substrate --</b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>
    </div>

    <div v-if="selectedSubstrate">
      <div v-if="loadingOptions" class="text-center">
        <b-spinner label="Loading options..."></b-spinner>
      </div>
      <div v-else-if="availableOptions.length === 0" class="alert alert-info">
        No options available for this substrate.
      </div>
      <div v-else>
        <div class="row">
          <div v-for="(options, category) in groupedOptions" :key="category" class="col-md-4 mb-4">

            <!-- Subcategory Dropdown (if applicable) -->
            <div v-if="getSubcategories(options).length > 0" class="mb-2">
              <b-form-group :label="category + ' Family'">
                <b-form-select v-model="selectedSubcategories[category]" :options="getSubcategories(options)">
                  <template #first>
                    <b-form-select-option :value="null">-- All {{ category }} Families --</b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </div>

            <!-- Main Options Dropdown -->
            <b-form-group :label="category">
              <b-form-select
                v-model="selectedOptions[category]"
                :options="getCategoryOptionsFiltered(options, category)"
              >
                <template #first>
                  <b-form-select-option :value="null">-- Select {{ category }} --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </div>
        </div>
      </div>
    </div>

    <div v-if="selectedSubstrate" class="mt-4">
      <h4>Item Data</h4>
      <b-form-textarea
        :value="JSON.stringify(itemData, null, 2)"
        rows="10"
        readonly
        style="font-family: monospace;"
        class="mb-3"
      ></b-form-textarea>

      <div class="d-flex justify-content-end align-items-center gap-3">
        <b-form-checkbox v-model="dryRun" class="mb-0">
          Dry run
        </b-form-checkbox>
        <b-button
          variant="primary"
          size="lg"
          @click="saveDesign"
          :disabled="saving"
        >
          <b-spinner small v-if="saving" class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save Design' }}
        </b-button>
      </div>
    </div>

    <div v-if="dryRun && dryRunOutput" class="mt-4">
      <h5>Dry Run Price Data</h5>
      <b-form-textarea
        :value="JSON.stringify(dryRunOutput, null, 2)"
        rows="10"
        readonly
        style="font-family: monospace;"
      ></b-form-textarea>
    </div>
  </div>
</template>

<script>
import { http, http2, getAppApi } from '@/services/http.js'
import { groupBy } from 'lodash'
import QuickAddWorkspace from '@/components/QuickAddWorkspace.vue'
import { mapState } from 'vuex'

export default {
  name: 'DesignStudio',
  components: {
    QuickAddWorkspace
  },
  data() {
    return {
      substrates: [],
      selectedSubstrate: null,
      availableOptions: [],
      selectedOptions: {},
      selectedSubcategories: {}, // Store selected subcategory for each category
      selectedImage: null,
      selectedWorkspace: null,
      dimensions: {
        width: '',
        height: ''
      },
      loadingSubstrates: false,
      loadingOptions: false,
      saving: false,
      pendingChosenOptions: null,
      dryRun: false,
      dryRunOutput: null,
    }
  },
  computed: {
    substrateOptions() {
      return this.substrates.map(s => ({
        value: s.id,
        text: s.name
      }))
    },
    groupedOptions() {
      // Group by category name (field 'name' in serializer)
      return groupBy(this.availableOptions, 'name')
    },
    currentSubstrateName() {
      const sub = this.substrates.find(s => s.id === this.selectedSubstrate)
      return sub ? sub.name : ''
    },
    itemData() {
      if (!this.selectedSubstrate) return {}

      const chosenOptions = []

      Object.entries(this.selectedOptions).forEach(([category, optionId]) => {
        if (!optionId) return

        // Find the option object in availableOptions
        const option = this.availableOptions.find(o => o.id === optionId)
        if (option) {
          chosenOptions.push({
            option_id: optionId,
            name: category,
            value: option.value || option.name
          })
        }
      })

      return {
        imagedata: this.selectedImage,
        size: {
          w: this.dimensions.width,
          h: this.dimensions.height
        },
        // API expects workspace as a string name and project_id as int
        project_id: this.selectedWorkspace?.project || this.profile?.current_project?.id || null,
        workspace: this.selectedWorkspace?.name || this.selectedWorkspace?.label || '',
        quantity: 1,
        config_data: {
          substrate: this.currentSubstrateName,
          width: this.dimensions.width,
          height: this.dimensions.height,
          chosen_options: chosenOptions
        }
      }
    },
    ...mapState('user', ['profile'])
  },
  watch: {
    selectedSubstrate(newVal) {
      this.selectedOptions = {}
      this.selectedSubcategories = {}
      if (newVal) {
        this.fetchOptions(newVal)
      } else {
        this.availableOptions = []
      }
    },
    '$route.query.item': {
      handler(itemId) {
        if (itemId) {
          this.loadItem(itemId)
        }
      },
      immediate: true,
    },
    availableOptions: {
      handler(opts) {
        if (opts.length && this.pendingChosenOptions?.length) {
          const selected = {}
          this.pendingChosenOptions.forEach(co => {
            const opt = opts.find(o =>
              o.id === co.option_id || (o.name === co.name && String(o.value || o.name) === String(co.value))
            )
            if (opt) selected[opt.name] = opt.id
          })
          this.selectedOptions = { ...this.selectedOptions, ...selected }
          this.pendingChosenOptions = null
        }
      },
    },
  },
  created() {
    this.fetchSubstrates()
  },
  methods: {
    async loadItem(itemId) {
      if (!itemId) return
      try {
        if (!this.substrates.length) {
          await this.fetchSubstrates()
        }

        const response = await http2.get(`api/v3/design-studio/items/${itemId}/`)
        const item = response.data

        this.selectedImage = item.imagedata || null
        this.dimensions = {
          width: String(item.width ?? item.config_data?.width ?? ''),
          height: String(item.height ?? item.config_data?.height ?? ''),
        }

        const substrateName = item.substrate || item.config_data?.substrate
        if (substrateName && this.substrates.length) {
          const sub = this.substrates.find(s => s.name?.toLowerCase() === String(substrateName).toLowerCase())
          this.selectedSubstrate = sub ? sub.id : null
          this.pendingChosenOptions = item.config_data?.chosen_options || null
        }

        const projectId = typeof item.project === 'object' ? item.project?.id : item.project
        if (projectId && item.workspace) {
          await this.loadWorkspaceForItem(projectId, item.workspace)
        }
      } catch (e) {
        console.error('Failed to load item', e)
        this.$bvToast.toast('Failed to load item', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async loadWorkspaceForItem(projectId, workspaceName) {
      try {
        const response = await getAppApi().get('/api/v2/myworkspaces/', {
          params: { page_size: 100 },
        })
        const results = response.data?.results || []
        const pid = Number(projectId)
        const ws = results.find(w =>
          Number(w.project) === pid && (w.name === workspaceName || w.label?.includes(workspaceName))
        )
        if (ws) this.selectedWorkspace = ws
      } catch {
        // Ignore - user can re-select workspace
      }
    },
    getSubcategories(options) {
      const groups = new Set()
      options.forEach(opt => {
        const cGroups = opt.extra_data && opt.extra_data.color_groups
        if (Array.isArray(cGroups)) {
          cGroups.forEach(g => groups.add(g))
        }
      })
      return Array.from(groups).sort().map(g => ({ value: g, text: g }))
    },
    getCategoryOptionsFiltered(options, category) {
      const selectedSub = this.selectedSubcategories[category]
      let filteredOptions = options

      if (selectedSub) {
        filteredOptions = options.filter(opt => {
          const cGroups = opt.extra_data && opt.extra_data.color_groups
          return Array.isArray(cGroups) && cGroups.includes(selectedSub)
        })
      }

      // Return simple mapped options since filtering is handled by the subcategory select
      return filteredOptions.map(opt => ({
        value: opt.id,
        text: opt.value || opt.name
      }))
    },
    // getCategoryOptions removed as it's replaced by getCategoryOptionsFiltered
    async fetchTestImage() {
      try {
        const response = await http.get('content/search/v3/', {
          params: { limit: 1 }
        })
        if (response.data.images && response.data.images.length > 0) {
          this.selectedImage = response.data.images[0]
        }
      } catch (e) {
        console.error('Failed to fetch test image', e)
        this.$bvToast.toast('Failed to load test image', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      }
    },
    async fetchSubstrates() {
      this.loadingSubstrates = true
      try {
        const response = await http2.get('api/v1/substrate/')
        // Handle pagination or direct array
        this.substrates = Array.isArray(response.data) ? response.data : (response.data.results || [])
      } catch (e) {
        console.error('Failed to fetch substrates', e)
        this.$bvToast.toast('Failed to load substrates', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loadingSubstrates = false
      }
    },
    async fetchOptions(substrateId) {
      this.loadingOptions = true
      try {
        const response = await http2.get('api/v3/design-studio/available-options/', {
          params: {
            substrate: substrateId,
            limit: 10000
          }
        })
        this.availableOptions = Array.isArray(response.data) ? response.data : (response.data.results || [])

        // Initialize selectedOptions with defaults or null
        const optionsByCategory = groupBy(this.availableOptions, 'name')
        const initialSelection = {}
        Object.keys(optionsByCategory).forEach(category => {
          const options = optionsByCategory[category]
          const defaultOpt = options.find(o => o.is_default)
          initialSelection[category] = defaultOpt ? defaultOpt.id : null
        })
        this.selectedOptions = initialSelection

      } catch (e) {
        console.error('Failed to fetch options', e)
        this.$bvToast.toast('Failed to load options', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
        this.availableOptions = []
      } finally {
        this.loadingOptions = false
      }
    },
    async saveDesign() {
      this.saving = true
      try {
        const payload = this.itemData
        // Using http2 for main API
        const itemId = this.$route.query.item
        const url = itemId
          ? `api/v3/design-studio/items/${itemId}/`
          : 'api/v3/design-studio/items/'

        const response = itemId
          ? await http2.patch(
              url,
              payload,
              { params: { dry_run: this.dryRun } }
            )
          : await http2.post(
              url,
              payload,
              { params: { dry_run: this.dryRun } }
            )

        if (this.dryRun) {
          this.dryRunOutput = response.data && response.data.price_data
        } else {
          this.dryRunOutput = null

          // If we just created a new item, redirect to URL with its id
          if (!itemId && response && response.data && response.data.id) {
            this.$router.replace({
              path: '/design-studio',
              query: { item: response.data.id },
            })
          }
        }

        this.$bvToast.toast('Design saved successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (e) {
        console.error('Failed to save design', e)
        this.$bvToast.toast('Failed to save design', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
